import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/Primitives";
import { Mail, Send, Save, Loader2, ShieldCheck, AlertCircle } from "lucide-react";

export default function EmailSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({
        api_key: "",
        from_email: "",
        from_name: "",
        is_enabled: true,
    });
    const [testTo, setTestTo] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/settings/email");
            setSettings(data);
            setForm({
                api_key: "",
                from_email: data.from_email || "",
                from_name: data.from_name || "",
                is_enabled: data.is_enabled ?? true,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load email settings");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                from_email: form.from_email.trim(),
                from_name: form.from_name.trim() || null,
                is_enabled: form.is_enabled,
                api_key: form.api_key.trim() || null,
            };
            const { data } = await api.put("/settings/email", payload);
            setSettings(data);
            setForm((f) => ({ ...f, api_key: "" }));
            toast.success("Email settings saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        if (!testTo.trim()) { toast.error("Enter a recipient first"); return; }
        setTesting(true);
        try {
            const { data } = await api.post("/settings/email/test", { to: testTo.trim() });
            toast.success(`Test email sent → ${testTo} (id: ${data.provider_message_id?.slice(0, 12) || "-"})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test send failed");
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return <div className="p-10 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>;
    }

    return (
        <div data-testid="email-settings-page">
            <PageHeader
                eyebrow="Super Admin"
                title="Email Settings"
                subtitle="Configure the Resend API used by the platform to deliver scheduled reports, margin alerts, and export emails."
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Status card */}
                <div className="lg:col-span-1">
                    <div className="bg-white border border-zinc-200 rounded p-5 space-y-3" data-testid="email-status-card">
                        <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-zinc-500" />
                            <div className="overline">Current status</div>
                        </div>
                        <div className="flex items-center gap-2">
                            {settings?.configured && settings?.is_enabled ? (
                                <><ShieldCheck className="h-4 w-4 text-emerald-600" /><span className="text-sm text-emerald-700 font-medium">Active</span></>
                            ) : settings?.configured ? (
                                <><AlertCircle className="h-4 w-4 text-amber-500" /><span className="text-sm text-amber-700 font-medium">Configured but disabled</span></>
                            ) : (
                                <><AlertCircle className="h-4 w-4 text-red-500" /><span className="text-sm text-red-700 font-medium">Not configured</span></>
                            )}
                        </div>
                        {settings?.api_key_masked && (
                            <div className="text-xs">
                                <div className="overline mb-1">API key</div>
                                <code className="text-zinc-700 bg-zinc-100 px-2 py-1 rounded" data-testid="email-key-mask">{settings.api_key_masked}</code>
                            </div>
                        )}
                        {settings?.from_email && (
                            <div className="text-xs">
                                <div className="overline mb-1">From</div>
                                <span className="text-zinc-800">{settings.from_name ? `${settings.from_name} <${settings.from_email}>` : settings.from_email}</span>
                            </div>
                        )}
                        {settings?.updated_at && (
                            <div className="text-[10px] text-zinc-400">
                                Last updated {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by_email || "—"}
                            </div>
                        )}
                    </div>
                </div>

                {/* Config form */}
                <div className="lg:col-span-2">
                    <form onSubmit={save} className="bg-white border border-zinc-200 rounded p-5 space-y-4" data-testid="email-config-form">
                        <div>
                            <label className="overline">Resend API key</label>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={form.api_key}
                                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                                placeholder={settings?.configured ? "•••••••••• (leave blank to keep existing)" : "re_XXXXXXXXXXXXXXXXXXXXXXXX"}
                                className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded font-mono text-sm"
                                data-testid="email-api-key-input"
                            />
                            <div className="text-[10px] text-zinc-400 mt-1">
                                Get your key at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">resend.com/api-keys</a>. Encrypted at rest; never visible to anyone else.
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="overline">From email</label>
                                <input
                                    type="email"
                                    required
                                    value={form.from_email}
                                    onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                                    placeholder="reports@yourbrand.com"
                                    className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded"
                                    data-testid="email-from-input"
                                />
                                <div className="text-[10px] text-zinc-400 mt-1">
                                    Must belong to a domain you&apos;ve verified in Resend.
                                </div>
                            </div>
                            <div>
                                <label className="overline">From name (optional)</label>
                                <input
                                    value={form.from_name}
                                    onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                                    placeholder="Lumina Reports"
                                    className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded"
                                    data-testid="email-from-name-input"
                                />
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.is_enabled}
                                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                                data-testid="email-enabled-toggle"
                            />
                            <span>Enabled — send real emails through Resend. Uncheck to pause delivery (falls back to logging only).</span>
                        </label>

                        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
                                style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }}
                                data-testid="email-save-btn"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Save settings
                            </button>
                        </div>
                    </form>

                    {/* Test send */}
                    <div className="mt-6 bg-white border border-zinc-200 rounded p-5" data-testid="email-test-card">
                        <div className="overline mb-2">Send a test email</div>
                        <div className="text-xs text-zinc-500 mb-3">
                            Uses the currently-saved config. Save first if you just entered a new API key.
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                placeholder="you@yourcompany.com"
                                className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded"
                                data-testid="email-test-to-input"
                            />
                            <button
                                type="button"
                                onClick={sendTest}
                                disabled={testing || !settings?.configured}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium border border-zinc-300 hover:border-zinc-400 disabled:opacity-50"
                                data-testid="email-test-send-btn"
                            >
                                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                Send test
                            </button>
                        </div>
                        {!settings?.configured && (
                            <div className="text-[10px] text-red-500 mt-2">Save an API key first.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
